/**
 * src/data/ModernNetworkingBank.js
 * Translated plain-English scenario bank for Module 1: Modern Networking (Spatial Audit / Directory Navigation).
 */

export const modernNetworkingBank = [
    {
    "id": "network_01",
    "scenarioContext": "You are finalizing the user interface for a new checkout flow, but need to confirm if the new data-capture fields violate customer privacy rules before launching to the public.",
    "options": [
    {
    "nodeTitle": "Data Privacy Officer (Legal)",
    "category": "Optimal",
    "feedback": "Correct. Bypassing gatekeepers to directly consult the horizontal SME prevents compliance failures and maintains velocity."
    },
    {
    "nodeTitle": "Director of Development (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Escalating a routine cross-functional compliance check to your Director drains leadership bandwidth."
    },
    {
    "nodeTitle": "Lead Database Administrator (Engineering)",
    "category": "Siloed Trap",
    "feedback": "Failure. The administrator manages the database structure, but Legal dictates privacy compliance. You stayed inside your silo."
    },
    {
    "nodeTitle": "Customer Success Manager",
    "category": "Irrelevant",
    "feedback": "Failure. Customer Success handles post-launch fallout, not pre-launch legal compliance."
    }
    ]
    },
    {
    "id": "network_02",
    "scenarioContext": "Your Q3 marketing campaign is experiencing a massive spike in ad costs, threatening to exceed the departmental budget. You need to secure a budget increase authorization.",
    "options": [
    {
    "nodeTitle": "Financial Forecaster (Finance)",
    "category": "Optimal",
    "feedback": "Correct. Finance owns the budget models. Engaging them directly allows for rapid variance analysis and horizontal reallocation."
    },
    {
    "nodeTitle": "Chief Marketing Officer (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Dumping a budget variance on the CMO without prior financial modeling forces them to do your administrative legwork."
    },
    {
    "nodeTitle": "Lead Media Buyer (Marketing)",
    "category": "Siloed Trap",
    "feedback": "Failure. Media buyers spend the money, they do not authorize systemic budget increases. You wasted time in an echo chamber."
    },
    {
    "nodeTitle": "User Experience Designer (Product)",
    "category": "Irrelevant",
    "feedback": "Failure. User experience design has zero bearing on marketing financial variances. Profound lack of systemic awareness."
    }
    ]
    },
    {
    "id": "network_03",
    "scenarioContext": "A critical enterprise client is demanding a custom contract with massive financial penalties for service downtime before they will sign a major deal.",
    "options": [
    {
    "nodeTitle": "Commercial Counsel (Legal)",
    "category": "Optimal",
    "feedback": "Correct. Legal owns corporate liability exposure. They can immediately redline the contract to protect the company without stalling the deal."
    },
    {
    "nodeTitle": "VP of Sales (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Your VP of Sales cannot practice law. Escalating to them simply creates a middleman who will have to email Legal anyway."
    },
    {
    "nodeTitle": "Pre-Sales Engineer (Sales)",
    "category": "Siloed Trap",
    "feedback": "Failure. Pre-Sales Engineers validate technical feasibility, not legal and financial liability thresholds."
    },
    {
    "nodeTitle": "Graphic Designer (Creative)",
    "category": "Irrelevant",
    "feedback": "Failure. Selecting a designer to review a massive liability contract is catastrophic."
    }
    ]
    },
    {
    "id": "network_04",
    "scenarioContext": "You want to integrate a powerful third-party software tool into the HR platform, but the tool requires uploading the personal email addresses of all employees.",
    "options": [
    {
    "nodeTitle": "Cybersecurity Risk Analyst (Security)",
    "category": "Optimal",
    "feedback": "Correct. Security must perform a vendor risk assessment on data handling before any personal information leaves the corporate systems."
    },
    {
    "nodeTitle": "Chief Human Resources Officer (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. The CHRO manages people, not digital data security. You've escalated a technical risk to an operational executive."
    },
    {
    "nodeTitle": "HR Business Partner (HR)",
    "category": "Siloed Trap",
    "feedback": "Failure. Consulting another HR peer about a severe data security risk is the definition of dangerous siloed thinking."
    },
    {
    "nodeTitle": "Facilities Manager (Operations)",
    "category": "Irrelevant",
    "feedback": "Failure. Physical security has nothing to do with digital vendor risk assessments."
    }
    ]
    },
    {
    "id": "network_05",
    "scenarioContext": "Customer Support has flagged a recurring system error that deletes user profiles, but you need to know which specific backend database system is failing to route the ticket accurately.",
    "options": [
    {
    "nodeTitle": "System Outage Triage Specialist (Engineering)",
    "category": "Optimal",
    "feedback": "Correct. The Triage Specialist possesses the systemic diagnostic skills to trace the database failure horizontally across the system."
    },
    {
    "nodeTitle": "Director of Customer Support (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Pushing a technical triage issue to your Director clogs the management chain with operational noise."
    },
    {
    "nodeTitle": "Customer Success Manager (Support)",
    "category": "Siloed Trap",
    "feedback": "Failure. Another CSM can only commiserate with you; they cannot diagnose backend database systems."
    },
    {
    "nodeTitle": "Event Coordinator (Marketing)",
    "category": "Irrelevant",
    "feedback": "Failure. Completely unrelated to software engineering or customer data loss."
    }
    ]
    },
    {
    "id": "network_06",
    "scenarioContext": "The Data Analysis team needs direct access to restricted historical financial records to build an internal revenue prediction model, bypassing standard security rules.",
    "options": [
    {
    "nodeTitle": "Data Compliance Officer (Compliance/IT)",
    "category": "Optimal",
    "feedback": "Correct. Access to restricted financial databases requires a formal governance exception and audit logging."
    },
    {
    "nodeTitle": "Chief Data Officer (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. The CDO relies on Compliance to enforce policy. Forcing them to intervene on a routine access request is poor autonomy."
    },
    {
    "nodeTitle": "Senior Data Analyst (Data Science)",
    "category": "Siloed Trap",
    "feedback": "Failure. Data Analysts execute the models, but they do not have the authority to bypass corporate financial data compliance."
    },
    {
    "nodeTitle": "User Experience Researcher (Design)",
    "category": "Irrelevant",
    "feedback": "Failure. Design researchers have no jurisdiction over secure financial database access controls."
    }
    ]
    },
    {
    "id": "network_07",
    "scenarioContext": "Operations is terminating a toxic employee immediately. You must ensure all of their software accounts, remote network access, and physical building badges are revoked simultaneously.",
    "options": [
    {
    "nodeTitle": "Digital Access Administrator (IT)",
    "category": "Optimal",
    "feedback": "Correct. The Administrator acts as the central horizontal hub for provisioning and revoking enterprise-wide digital access."
    },
    {
    "nodeTitle": "Chief Operating Officer (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Alerting the COO to a termination is fine, but asking them to handle system de-provisioning is an absurd misallocation of executive time."
    },
    {
    "nodeTitle": "Operations Coordinator (Operations)",
    "category": "Siloed Trap",
    "feedback": "Failure. An Ops coordinator cannot revoke cross-departmental software and network credentials."
    },
    {
    "nodeTitle": "Search Engine Specialist (Marketing)",
    "category": "Irrelevant",
    "feedback": "Failure. Marketing has zero involvement in offboarding technical access."
    }
    ]
    },
    {
    "id": "network_08",
    "scenarioContext": "A PR crisis is unfolding because the core product went down globally. The communications team needs an exact estimated time to recovery to brief the press.",
    "options": [
    {
    "nodeTitle": "System Outage Commander (Engineering)",
    "category": "Optimal",
    "feedback": "Correct. The Commander is the ground truth for system health and recovery timelines during a major system outage."
    },
    {
    "nodeTitle": "VP of Communications (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Your VP needs the timeline from you; asking them for it exposes a complete lack of cross-functional resourcefulness."
    },
    {
    "nodeTitle": "Social Media Manager (Communications)",
    "category": "Siloed Trap",
    "feedback": "Failure. Asking another comms peer for a technical timeline is the blind leading the blind. You must bridge the gap to Engineering."
    },
    {
    "nodeTitle": "Corporate Recruiter (HR)",
    "category": "Irrelevant",
    "feedback": "Failure. HR plays no role in technical crisis response."
    }
    ]
    },
    {
    "id": "network_09",
    "scenarioContext": "You want to offer a massive 60% discount to save a churning major account, but doing so pushes their account profitability into negative territory.",
    "options": [
    {
    "nodeTitle": "Pricing Strategy Analyst (Finance)",
    "category": "Optimal",
    "feedback": "Correct. Pricing Strategy specializes in structuring non-standard pricing to balance retention metrics against margin loss."
    },
    {
    "nodeTitle": "VP of Customer Success (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Escalating a pricing calculation to the VP without an approved financial model forces them to make a blind decision."
    },
    {
    "nodeTitle": "Account Executive (Sales)",
    "category": "Siloed Trap",
    "feedback": "Failure. Sales will always say yes to the discount, but they don't own the post-sale margin implications. You are acting in a silo."
    },
    {
    "nodeTitle": "Lead Animator (Creative)",
    "category": "Irrelevant",
    "feedback": "Failure. Selecting an animator to handle complex financial discounting is a profound failure of basic enterprise literacy."
    }
    ]
    },
    {
    "id": "network_10",
    "scenarioContext": "A developer needs to push an urgent software fix to the live product immediately, but the automated deployment system is blocked by a rigid security scan.",
    "options": [
    {
    "nodeTitle": "Cybersecurity Compliance Lead (Security/IT)",
    "category": "Optimal",
    "feedback": "Correct. The Cybersecurity Lead operates at the intersection of software deployment and security, and can authorize system bypasses or fast-track scan reviews."
    },
    {
    "nodeTitle": "Director of Engineering (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Escalating a routine deployment block to the Director slows down the fix and punts your operational roadblock upward."
    },
    {
    "nodeTitle": "Software Testing Engineer (Engineering)",
    "category": "Siloed Trap",
    "feedback": "Failure. Testing Engineers handle functional quality, not overriding mandatory cybersecurity compliance gateways."
    },
    {
    "nodeTitle": "Copywriter (Marketing)",
    "category": "Irrelevant",
    "feedback": "Failure. Marketing has zero visibility or authority over software deployment systems."
    }
    ]
    },
    {
    "id": "network_11",
    "scenarioContext": "Procurement wants to switch digital file hosting providers to save 15%, but needs to know if the migration will cause unacceptable system downtime for the company.",
    "options": [
    {
    "nodeTitle": "Digital Infrastructure Architect (Engineering)",
    "category": "Optimal",
    "feedback": "Correct. The architect can horizontally translate a financial procurement decision into an objective technical risk assessment."
    },
    {
    "nodeTitle": "Chief Procurement Officer (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. The CPO cares about the 15% savings; you escalated without arming them with the technical migration risk."
    },
    {
    "nodeTitle": "Vendor Contract Manager (Procurement)",
    "category": "Siloed Trap",
    "feedback": "Failure. A vendor manager negotiates the contract, they cannot estimate system downtime. You avoided cross-departmental alignment."
    },
    {
    "nodeTitle": "Intellectual Property Counsel (Legal)",
    "category": "Irrelevant",
    "feedback": "Failure. IP Legal does not estimate server migration downtime."
    }
    ]
    },
    {
    "id": "network_12",
    "scenarioContext": "HR is launching a company-wide diversity initiative and needs to ensure the internal messaging perfectly aligns with the company's external public relations commitments.",
    "options": [
    {
    "nodeTitle": "Corporate Communications Lead (PR/Comms)",
    "category": "Optimal",
    "feedback": "Correct. Corporate Comms synchronizes internal initiatives with external market messaging to prevent brand whiplash."
    },
    {
    "nodeTitle": "Chief Diversity Officer (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Relying entirely on your executive leader for external alignment creates an echo chamber and ignores public relations risk."
    },
    {
    "nodeTitle": "HR Generalist (HR)",
    "category": "Siloed Trap",
    "feedback": "Failure. Checking messaging with another HR employee does not secure external PR alignment."
    },
    {
    "nodeTitle": "Database Administrator (Engineering)",
    "category": "Irrelevant",
    "feedback": "Failure. Database administrators do not write corporate diversity messaging."
    }
    ]
    },
    {
    "id": "network_13",
    "scenarioContext": "The web development team is building a new landing page and needs the standardized brand colors and font rules for the new visual rollout.",
    "options": [
    {
    "nodeTitle": "Brand Design Director (Design)",
    "category": "Optimal",
    "feedback": "Correct. The Brand Design Director horizontally governs the visual design standards across all web teams."
    },
    {
    "nodeTitle": "Engineering Manager (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Your manager does not possess the brand color codes. Escalating this shows a lack of basic resourcefulness."
    },
    {
    "nodeTitle": "Backend Database Developer (Engineering)",
    "category": "Siloed Trap",
    "feedback": "Failure. Asking a backend peer for brand typography is staying in your comfort zone while entirely missing the objective."
    },
    {
    "nodeTitle": "Financial Auditor (Finance)",
    "category": "Irrelevant",
    "feedback": "Failure. Finance has no involvement in front-end design specifications."
    }
    ]
    },
    {
    "id": "network_14",
    "scenarioContext": "A new product feature requires changing the standard Terms of Service contract to limit corporate legal liability.",
    "options": [
    {
    "nodeTitle": "Product Liability Counsel (Legal)",
    "category": "Optimal",
    "feedback": "Correct. Product Counsel specifically embeds with development to translate feature launches into legal contract protection."
    },
    {
    "nodeTitle": "VP of Product (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Your VP cannot rewrite a binding legal contract. You have escalated a problem rather than a legally vetted solution."
    },
    {
    "nodeTitle": "Product Marketing Manager (Marketing)",
    "category": "Siloed Trap",
    "feedback": "Failure. Product Marketers write marketing copy, not legally binding liability waivers."
    },
    {
    "nodeTitle": "Logistics Coordinator (Supply Chain)",
    "category": "Irrelevant",
    "feedback": "Failure. Supply Chain is entirely irrelevant to software Terms of Service."
    }
    ]
    },
    {
    "id": "network_15",
    "scenarioContext": "You are organizing a 500-person company offsite and need clearance for a high-risk team-building activity involving off-road vehicles.",
    "options": [
    {
    "nodeTitle": "Corporate Risk Officer (Legal/Finance)",
    "category": "Optimal",
    "feedback": "Correct. Risk Management horizontally assesses corporate liability insurance limits for high-exposure physical events."
    },
    {
    "nodeTitle": "VP of Human Resources (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Forcing your VP to make a massive liability gamble without Risk Management's clearance is reckless."
    },
    {
    "nodeTitle": "Events Manager (HR)",
    "category": "Siloed Trap",
    "feedback": "Failure. Another event planner cannot underwrite corporate liability insurance. You are seeking comfort in your silo."
    },
    {
    "nodeTitle": "Software Deployment Lead (Engineering)",
    "category": "Irrelevant",
    "feedback": "Failure. Engineering manages servers, not physical offsite event liability."
    }
    ]
    },
    {
    "id": "network_16",
    "scenarioContext": "A major competitor just dropped their pricing by 20%. Product management needs a rapid competitive analysis to see if we should match it or hold our ground.",
    "options": [
    {
    "nodeTitle": "Market Intelligence Analyst (Strategy/Marketing)",
    "category": "Optimal",
    "feedback": "Correct. Market Intelligence specializes in horizontal data gathering to counter competitor maneuvers swiftly."
    },
    {
    "nodeTitle": "Chief Product Officer (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Asking the CPO what to do before gathering the competitive data forces leadership to operate blindly."
    },
    {
    "nodeTitle": "Interface Designer (Product)",
    "category": "Siloed Trap",
    "feedback": "Failure. Interface Designers handle layout, not market pricing elasticity and competitor strategy."
    },
    {
    "nodeTitle": "Facility Security Officer (Ops)",
    "category": "Irrelevant",
    "feedback": "Failure. Physical security has no insight into competitor pricing strategies."
    }
    ]
    },
    {
    "id": "network_17",
    "scenarioContext": "A sales team in Germany requires the latest marketing presentation to be heavily translated and culturally adapted for a major pitch tomorrow.",
    "options": [
    {
    "nodeTitle": "Global Localization Manager (Marketing/Ops)",
    "category": "Optimal",
    "feedback": "Correct. Localization acts as the horizontal bridge between global asset creation and regional cultural accuracy."
    },
    {
    "nodeTitle": "VP of EMEA Sales (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Punting asset translation up to a Sales VP is a gross mismanagement of executive time and delays the pitch."
    },
    {
    "nodeTitle": "Regional Sales Director (Sales)",
    "category": "Siloed Trap",
    "feedback": "Failure. A Sales Director might speak German, but they are not equipped to redesign and translate corporate marketing materials."
    },
    {
    "nodeTitle": "Internal IT Network Administrator (IT)",
    "category": "Irrelevant",
    "feedback": "Failure. IT networks have nothing to do with German sales asset translation."
    }
    ]
    },
    {
    "id": "network_18",
    "scenarioContext": "The analytics team notices a sudden, severe 30% drop in mobile purchase rates immediately following a routine mobile phone software update.",
    "options": [
    {
    "nodeTitle": "Mobile Software Release Manager (Engineering)",
    "category": "Optimal",
    "feedback": "Correct. The Mobile Software Release Manager bridges the gap between mobile operating system updates and internal software functionality."
    },
    {
    "nodeTitle": "Head of Analytics (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Your manager can only stare at the same falling metrics you are. You must escalate horizontally to the fixers."
    },
    {
    "nodeTitle": "Data Analyst (Analytics)",
    "category": "Siloed Trap",
    "feedback": "Failure. Discussing bad data with another data analyst will not fix the broken mobile code."
    },
    {
    "nodeTitle": "Logistics Coordinator (Supply Chain)",
    "category": "Irrelevant",
    "feedback": "Failure. Supply Chain is disconnected from mobile conversion bugs."
    }
    ]
    },
    {
    "id": "network_19",
    "scenarioContext": "You are closing out the fiscal year and need to officially record revenue for a multi-year software contract that has complex upfront fees.",
    "options": [
    {
    "nodeTitle": "Financial Compliance Controller (Finance)",
    "category": "Optimal",
    "feedback": "Correct. The Financial Compliance Controller horizontally aligns sales contracts with strict accounting compliance rules."
    },
    {
    "nodeTitle": "VP of Sales (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Sales leadership does not dictate formal accounting rules. Escalating here creates compliance risk."
    },
    {
    "nodeTitle": "Account Executive (Sales)",
    "category": "Siloed Trap",
    "feedback": "Failure. Account Executives close deals, but they cannot legally determine complex revenue recognition schedules."
    },
    {
    "nodeTitle": "Brand Strategist (Marketing)",
    "category": "Irrelevant",
    "feedback": "Failure. Brand strategy has zero authority over financial accounting standards."
    }
    ]
    },
    {
    "id": "network_20",
    "scenarioContext": "You want to bypass standard background check rules to rush-hire a critical executive candidate who is demanding a 24-hour offer turnaround.",
    "options": [
    {
    "nodeTitle": "Employment Law Counsel (Legal)",
    "category": "Optimal",
    "feedback": "Correct. Bypassing compliance rules for executives carries massive corporate liability. Legal must approve the exception."
    },
    {
    "nodeTitle": "Chief Human Resources Officer (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Asking your CHRO to break protocol without a legal risk assessment forces them to operate recklessly."
    },
    {
    "nodeTitle": "Talent Acquisition Lead (HR)",
    "category": "Siloed Trap",
    "feedback": "Failure. Recruiters want to close the hire, but they do not have the authority to waive corporate liability checks."
    },
    {
    "nodeTitle": "Lead Software Engineer (Engineering)",
    "category": "Irrelevant",
    "feedback": "Failure. Engineering has no say in executive HR background check policies."
    }
    ]
    },
    {
    "id": "network_21",
    "scenarioContext": "Your major client is refusing to renew their contract unless they see the finalized quarterly development plan to ensure a specific feature is being built.",
    "options": [
    {
    "nodeTitle": "Product Marketing Manager (Product)",
    "category": "Optimal",
    "feedback": "Correct. Product Marketers translate engineering plans into customer-facing, legally safe materials for the sales field."
    },
    {
    "nodeTitle": "Sales Director (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Escalating to the Sales Director just creates an angry middleman who will demand the plan from Product anyway."
    },
    {
    "nodeTitle": "Sales Training Specialist (Sales)",
    "category": "Siloed Trap",
    "feedback": "Failure. Training Specialists prepare representatives; they do not dictate the actual engineering timeline or product roadmap."
    },
    {
    "nodeTitle": "IT Helpdesk Administrator (IT)",
    "category": "Irrelevant",
    "feedback": "Failure. IT Helpdesk has zero visibility into software development roadmaps."
    }
    ]
    },
    {
    "id": "network_22",
    "scenarioContext": "The technology team wants to open a new digital server location in Frankfurt, Germany to speed up the website, but isn't sure about the corporate tax implications of establishing a presence there.",
    "options": [
    {
    "nodeTitle": "International Tax Controller (Finance)",
    "category": "Optimal",
    "feedback": "Correct. Establishing a physical or digital presence in a new country triggers complex tax liabilities that Finance must clear horizontally."
    },
    {
    "nodeTitle": "Chief Technology Officer (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Pushing international tax law questions to your Chief Technology Officer is an absurd misallocation of technical leadership."
    },
    {
    "nodeTitle": "Server Deployment Lead (Engineering)",
    "category": "Siloed Trap",
    "feedback": "Failure. The Deployment Lead can start the server, but they are entirely unqualified to assess German corporate tax exposure."
    },
    {
    "nodeTitle": "Meeting Facilitator (Operations)",
    "category": "Irrelevant",
    "feedback": "Failure. Meeting facilitators organize schedules, they do not advise on international tax law."
    }
    ]
    },
    {
    "id": "network_23",
    "scenarioContext": "You are opening a satellite office in Tokyo and need to understand the local legally required employee benefits and severance rules before making job offers.",
    "options": [
    {
    "nodeTitle": "Global HR Benefits Manager (HR)",
    "category": "Optimal",
    "feedback": "Correct. This SME specializes in navigating international labor laws and aligning them horizontally with corporate HR policy."
    },
    {
    "nodeTitle": "VP of Global Expansion (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Your VP expects you to bring them the finalized benefits package, not to do the HR research for you."
    },
    {
    "nodeTitle": "Real Estate Manager (Operations)",
    "category": "Siloed Trap",
    "feedback": "Failure. Operations can secure the Tokyo office building, but they are unqualified to dictate Japanese labor laws."
    },
    {
    "nodeTitle": "Lead 3D Artist (Creative)",
    "category": "Irrelevant",
    "feedback": "Failure. Completely irrelevant to international HR law."
    }
    ]
    },
    {
    "id": "network_24",
    "scenarioContext": "A critical supplier just breached their contract by failing to deliver computer hardware on time. You need to know if you can legally withhold payment without being sued.",
    "options": [
    {
    "nodeTitle": "Commercial Litigation Counsel (Legal)",
    "category": "Optimal",
    "feedback": "Correct. Legal must immediately assess the breach-of-contract clauses before you take aggressive financial action."
    },
    {
    "nodeTitle": "Chief Procurement Officer (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. The CPO manages the vendor relationship, but withholding payment without Legal's clearance risks a severe lawsuit."
    },
    {
    "nodeTitle": "Supply Chain Analyst (Procurement)",
    "category": "Siloed Trap",
    "feedback": "Failure. An analyst tracks the delayed shipment, they cannot authorize breaking a legal payment obligation."
    },
    {
    "nodeTitle": "PR Manager (Communications)",
    "category": "Irrelevant",
    "feedback": "Failure. Public Relations has no involvement in business-to-business vendor contract disputes."
    }
    ]
    },
    {
    "id": "network_25",
    "scenarioContext": "Marketing wants to use a popular internet image featuring a celebrity in a massive global ad campaign, but needs to ensure it won't result in a lawsuit.",
    "options": [
    {
    "nodeTitle": "Intellectual Property Counsel (Legal)",
    "category": "Optimal",
    "feedback": "Correct. IP Counsel must horizontally clear commercial usage rights for external entities to prevent massive copyright lawsuits."
    },
    {
    "nodeTitle": "VP of Marketing (Direct Manager)",
    "category": "Decision Drift Trap",
    "feedback": "Failure. Escalating a legal copyright question to a Marketing VP creates a blind spot and risks a disastrous ad launch."
    },
    {
    "nodeTitle": "Senior Copywriter (Marketing)",
    "category": "Siloed Trap",
    "feedback": "Failure. Copywriters write the promotional text, they do not possess the legal authority to clear celebrity likeness rights."
    },
    {
    "nodeTitle": "Payroll Administrator (HR)",
    "category": "Irrelevant",
    "feedback": "Failure. Payroll has absolutely nothing to do with external copyright law."
    }
    ]
    }
    ]